import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    Box,
    Typography
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../context/AuthContext';
import { getLanguageOptions } from '../constants/enums';

export function LanguageMenuButton({ sx, currency: currencyProp }) {
    const { currentLanguage, changeLanguage, preferredCurrency } = useAuth();
    const currency = currencyProp || preferredCurrency;
    const languageOptions = useMemo(() => getLanguageOptions(currency), [currency]);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (langCode) => {
        changeLanguage(langCode);
        handleClose();
    };

    const currentOption = languageOptions.find(opt => opt.value === currentLanguage) || languageOptions[0];

    return (
        <>
            <Tooltip title={`Language: ${currentOption.nativeLabel}`}>
                <IconButton
                    onClick={handleClick}
                    size="small"
                    aria-label="select language"
                    aria-controls={open ? 'language-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    sx={{
                        color: 'text.secondary',
                        p: 1,
                        borderRadius: 1.5,
                        '&:hover': {
                            backgroundColor: 'action.hover',
                            color: 'primary.main'
                        },
                        ...sx
                    }}
                >
                    <LanguageIcon fontSize="small" />
                    <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 600, display: { xs: 'none', sm: 'inline' } }}>
                        {currentOption.flag}
                    </Typography>
                </IconButton>
            </Tooltip>
            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        minWidth: 180,
                        mt: 1,
                        borderRadius: 2,
                        '& .MuiMenuItem-root': {
                            py: 1,
                            px: 1.5
                        }
                    }
                }}
            >
                {languageOptions.map((option) => {
                    const isSelected = option.value === currentLanguage;
                    return (
                        <MenuItem
                            key={option.value}
                            selected={isSelected}
                            onClick={() => handleSelect(option.value)}
                        >
                            <ListItemIcon sx={{ fontSize: '1.2rem', minWidth: 32 }}>
                                {option.flag}
                            </ListItemIcon>
                            <ListItemText
                                primary={option.nativeLabel}
                                secondary={option.label !== option.nativeLabel ? option.label : undefined}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: isSelected ? 600 : 400
                                }}
                            />
                            {isSelected && (
                                <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

export function LanguageSelectField({ value, onChange, disabled, fullWidth = true, hideLabel = false, sx, currency: currencyProp }) {
    const { currentLanguage, changeLanguage, preferredCurrency } = useAuth();
    const activeValue = value !== undefined ? value : currentLanguage;
    const currency = currencyProp || preferredCurrency;
    const languageOptions = useMemo(() => getLanguageOptions(currency), [currency]);

    const handleChange = (e) => {
        const selected = e.target.value;
        if (onChange) {
            onChange(e);
        } else {
            changeLanguage(selected);
        }
    };

    return (
        <FormControl fullWidth={fullWidth} sx={sx}>
            {!hideLabel && (
                <InputLabel id="language-select-label">Preferred Language / 偏好语言</InputLabel>
            )}
            <Select
                labelId={!hideLabel ? "language-select-label" : undefined}
                value={activeValue}
                label={!hideLabel ? "Preferred Language / 偏好语言" : undefined}
                onChange={handleChange}
                disabled={disabled}
            >
                {languageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{option.flag}</span>
                            <span>{option.nativeLabel} ({option.label})</span>
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default LanguageMenuButton;
